import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import WebApp from "@twa-dev/sdk";
import { API_URL } from "../config";
import { useUser } from "../context/UserContext";
import { FaGem, FaStar } from "react-icons/fa";
import { useTitle } from "../hooks/useTitle";
import haptic from "../utils/haptic";
import confetti from "canvas-confetti";
import { toast } from "react-toastify";
import ScratchCard from "../components/ScratchCard";

const SCRATCH_COST = 50;

function ScratchGame() {
  useTitle("Scratch Card");
  const navigate = useNavigate();
  const { userData, setUserData, APIKey } = useUser();
  
  const [phase, setPhase] = useState("idle"); // idle | buying | scratching | result
  const [result, setResult] = useState(null);

  const handleBuy = async () => {
    if ((userData.user?.points || 0) < SCRATCH_COST) {
      toast.error("Not enough Gems! You need 50 Gems to buy a scratch card.");
      haptic.error();
      return;
    }
    
    haptic.tap();
    setPhase("buying");

    try {
      const res = await axios.post(`${API_URL}/api/game/scratch/play`, {
        userId: userData.userId,
        initData: WebApp.initData,
      }, { headers: { "x-api-key": APIKey } });

      setResult(res.data);
      setPhase("scratching");
      
      // Deduct cost locally immediately for snappy UI
      setUserData(prev => ({
        ...prev,
        user: { ...prev.user, points: prev.user.points - SCRATCH_COST },
      }));

    } catch (err) {
      setPhase("idle");
      toast.error(err.response?.data?.error || "Error buying scratch card.");
    }
  };

  const handleComplete = () => {
    if (phase !== "scratching") return;
    
    // Scratched! Reveal the result properly.
    setPhase("result");
    
    // Update balance to final points from server
    setUserData(prev => ({
      ...prev,
      user: { ...prev.user, points: result.user.points },
    }));

    if (result.isJackpot) {
      haptic.success();
      confetti({ particleCount: 250, spread: 120, origin: { y: 0.5 }, colors: ["#ffd700", "#ff69b4", "#00ffff", "#ffffff"] });
    } else if (result.wonAmount >= 60) {
      haptic.success();
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    } else if (result.wonAmount > 0) {
      haptic.tap();
    } else {
      haptic.error();
    }
  };

  const handleReset = () => {
    setPhase("idle");
    setResult(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-[#0a0f1a] text-white min-h-screen pb-28 pt-20"
    >
      <div className="container mx-auto px-4 mt-2">
        <div className="flex flex-col items-center">

          {/* Jackpot Header */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur px-6 py-2 rounded-full border border-cyan-500/30 mb-6 flex items-center gap-3 shadow-[0_0_15px_rgba(6,182,212,0.15)] animate-pulse">
            <span className="text-cyan-400 font-black tracking-widest uppercase text-sm">🎰 Jackpot:</span>
            <span className="font-black text-cyan-300 flex items-center gap-1 text-2xl drop-shadow-md">
              500 <FaGem size={18} />
            </span>
          </div>

          <div className="relative flex flex-col items-center justify-center w-full max-w-sm mb-6">
            
            {phase === "idle" && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }} 
                className="w-64 h-64 bg-slate-800 rounded-3xl border-4 border-slate-700 shadow-2xl flex flex-col items-center justify-center p-6 text-center"
              >
                <div className="text-5xl mb-4">🎫</div>
                <h3 className="text-xl font-black text-slate-300 mb-2">Scratch & Win</h3>
                <p className="text-xs text-slate-400 font-medium leading-relaxed">Match 3 symbols to win up to 100 💎, or hit 3 💎 for the global Jackpot!</p>
              </motion.div>
            )}

            {phase === "buying" && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="w-64 h-64 bg-slate-800 rounded-3xl border-4 border-slate-700 flex flex-col items-center justify-center"
              >
                <div className="flex gap-2 mb-4">
                  {[0, 1, 2].map(i => (
                    <motion.div key={i} className="w-3 h-3 rounded-full bg-cyan-400" animate={{ y: [-6, 0, -6] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />
                  ))}
                </div>
                <p className="text-slate-400 font-semibold text-sm">Purchasing...</p>
              </motion.div>
            )}

            {(phase === "scratching" || phase === "result") && result && (
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative"
              >
                {phase === "scratching" && (
                  <div className="absolute -top-10 w-full text-center text-cyan-300 font-bold animate-pulse">
                    Scratch Here! 👆
                  </div>
                )}
                
                <ScratchCard
                  width={280}
                  height={280}
                  onComplete={handleComplete}
                  finishPercent={50}
                  brushSize={35}
                >
                  <div className="w-full h-full bg-slate-100 rounded-2xl grid grid-cols-3 gap-2 p-3 border-4 border-slate-300 shadow-inner">
                    {result.grid?.map((symbol, idx) => (
                      <div key={idx} className="bg-white rounded-xl flex items-center justify-center text-4xl shadow-sm border border-slate-200">
                        {symbol}
                      </div>
                    ))}
                  </div>
                </ScratchCard>
              </motion.div>
            )}
          </div>

          {/* Action Button / Result area */}
          <div className="w-full max-w-xs min-h-[120px]">
            <AnimatePresence mode="wait">
              {phase === "idle" && (
                <motion.div key="idle" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="flex flex-col items-center gap-4">
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={handleBuy}
                    disabled={(userData.user?.points || 0) < SCRATCH_COST}
                    className="w-full py-5 font-black text-xl rounded-3xl bg-gradient-to-r from-cyan-400 to-blue-500 text-white shadow-xl shadow-cyan-500/40 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Buy Card — 50 💎
                  </motion.button>
                  <button onClick={() => navigate("/game")} className="font-semibold text-slate-400 hover:text-white transition-colors text-sm">
                    Back to Hub
                  </button>
                </motion.div>
              )}

              {phase === "result" && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex flex-col items-center gap-4"
                >
                  {result.isJackpot ? (
                    <div className="w-full text-center bg-gradient-to-br from-yellow-400/20 to-orange-500/20 border-2 border-amber-400 rounded-3xl p-6 shadow-[0_0_30px_rgba(245,158,11,0.3)]">
                      <div className="text-5xl mb-2">🎉</div>
                      <p className="text-amber-300 font-black text-2xl mb-1">JACKPOT!</p>
                      <p className="text-white font-bold text-lg">You won <span className="text-amber-400 text-3xl font-black">500 💎</span></p>
                      <div className="flex justify-center gap-1 mt-3">
                        {[...Array(5)].map((_, i) => <FaStar key={i} className="text-amber-400" size={18} />)}
                      </div>
                    </div>
                  ) : (() => {
                    const isProfit = result.wonAmount >= SCRATCH_COST;
                    const diff = Math.abs(result.wonAmount - SCRATCH_COST);
                    return (
                      <div className={`w-full text-center rounded-3xl p-5 border-2 ${
                        isProfit
                          ? "bg-emerald-500/15 border-emerald-400"
                          : "bg-red-500/15 border-red-500"
                      }`}>
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-3 ${
                          isProfit ? "bg-emerald-500/30 text-emerald-300" : "bg-red-500/30 text-red-300"
                        }`}>
                          {isProfit ? "✦ WINNER" : "✕ BAD LUCK"}
                        </div>
                        <p className={`font-black text-4xl mb-2 ${
                          isProfit ? "text-emerald-400" : "text-slate-300"
                        }`}>
                          {result.wonAmount} 💎
                        </p>
                        <p className={`text-sm font-bold ${
                          isProfit ? "text-emerald-400" : "text-red-400"
                        }`}>
                          {isProfit ? `+${diff} profit` : `-${diff} lost`}
                        </p>
                      </div>
                    );
                  })()}

                  <div className="flex gap-3 w-full">
                    <button onClick={handleReset} className="flex-1 py-4 font-bold rounded-2xl bg-cyan-600 hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-500/30">
                      Play Again
                    </button>
                    <button onClick={() => navigate("/game")} className="flex-1 py-4 font-bold rounded-2xl bg-slate-800 hover:bg-slate-700 transition-colors text-slate-300">
                      Hub
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </motion.div>
  );
}

export default ScratchGame;
